/**
 * Payouts Admin Page
 *
 * Lists all seller payouts, pending sellers, and allows viewing payout status.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Wallet,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Users,
} from 'lucide-react';
import { api } from '../lib/api';

async function fetchFinance(path: string) {
  const res = await api.get(`/api/v1/admin/finance${path}`);
  return res.data;
}

export default function PayoutsAdminPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFinance(`/payouts${filter ? `?status=${filter}` : ''}`);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load payouts');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusIcon = (status: string) => {
    switch (status) {
      case 'PAID': return <CheckCircle2 className="w-3 h-3 text-green-400" />;
      case 'PENDING': return <Clock className="w-3 h-3 text-yellow-400" />;
      case 'PROCESSING': return <Loader2 className="w-3 h-3 animate-spin text-blue-400" />;
      case 'FAILED': return <XCircle className="w-3 h-3 text-red-400" />;
      default: return <AlertCircle className="w-3 h-3 text-gray-400" />;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'bg-green-500/10 text-green-400';
      case 'PENDING': return 'bg-yellow-500/10 text-yellow-400';
      case 'PROCESSING': return 'bg-blue-500/10 text-blue-400';
      case 'FAILED': return 'bg-red-500/10 text-red-400';
      default: return 'bg-gray-500/10 text-gray-400';
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Seller Payouts</h1>
          <p className="text-sm text-gray-400 mt-1">Manage monthly seller payouts and earnings</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors self-start sm:self-auto">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['', 'PENDING', 'PROCESSING', 'PAID', 'FAILED'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              filter === s
                ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
        </div>
      ) : (
        <>
          {/* Pending Sellers */}
          {data?.pendingSellers?.length > 0 && (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-yellow-400" />
                <h3 className="text-sm font-semibold text-white">Sellers with Pending Earnings</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="pb-2 pr-4">Seller</th>
                      <th className="pb-2 pr-4">Pending</th>
                      <th className="pb-2 pr-4">Total Earned</th>
                      <th className="pb-2 pr-4">Stripe</th>
                      <th className="pb-2">Bank</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {data.pendingSellers.map((s: any) => (
                      <tr key={s.userId} className="border-t border-gray-700/50">
                        <td className="py-2 pr-4">
                          <p className="text-white font-medium">{s.name || 'Unknown'}</p>
                          <p className="text-gray-500">{s.email}</p>
                        </td>
                        <td className="py-2 pr-4 font-mono text-yellow-400">${s.pendingEarnings.toFixed(2)}</td>
                        <td className="py-2 pr-4 font-mono">${s.totalEarnings.toFixed(2)}</td>
                        <td className="py-2 pr-4">
                          {s.stripeOnboardingComplete ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                        </td>
                        <td className="py-2">
                          {s.bankAccountVerified ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payout History */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-white">Payout Records</h3>
            </div>
            {(data?.payouts ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">No payouts recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="pb-2 pr-4">Period</th>
                      <th className="pb-2 pr-4">Seller</th>
                      <th className="pb-2 pr-4">Gross</th>
                      <th className="pb-2 pr-4">Fee</th>
                      <th className="pb-2 pr-4">Net Payout</th>
                      <th className="pb-2 pr-4">Status</th>
                      <th className="pb-2">Date</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {data.payouts.map((p: any) => (
                      <tr key={p.id} className="border-t border-gray-700/50">
                        <td className="py-2 pr-4 font-mono">{p.billingPeriod}</td>
                        <td className="py-2 pr-4">
                          <p className="text-white">{p.seller.name || 'Unknown'}</p>
                          <p className="text-gray-500">{p.seller.email}</p>
                        </td>
                        <td className="py-2 pr-4 font-mono">${p.grossEarnings.toFixed(2)}</td>
                        <td className="py-2 pr-4 font-mono text-gray-500">${p.platformFee.toFixed(2)}</td>
                        <td className="py-2 pr-4 font-mono text-white font-bold">${p.netPayout.toFixed(2)}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor(p.status)}`}>
                            {statusIcon(p.status)}
                            {p.status}
                          </span>
                        </td>
                        <td className="py-2 text-gray-500">
                          {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
